import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Plus, X, Loader2, ScanLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { extractPhotos } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

const MAX_SIDE = 2000;

const compress = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((blob) => { URL.revokeObjectURL(url); resolve(blob ? new File([blob], `page-${Date.now()}.jpg`, { type: "image/jpeg" }) : file); }, "image/jpeg", 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export const CameraCapture = ({ user, disabled }) => {
  const [pages, setPages] = useState([]); // [{file, url}]
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const addPhotos = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    if (!list.length) return;
    const compressed = await Promise.all(list.map(compress));
    setPages((p) => [...p, ...compressed.map((f) => ({ file: f, url: URL.createObjectURL(f) }))].slice(0, 10));
    if (inputRef.current) inputRef.current.value = "";
  };
  const remove = (i) => setPages((p) => { URL.revokeObjectURL(p[i].url); return p.filter((_, k) => k !== i); });

  const read = async () => {
    if (!pages.length) return;
    setBusy(true);
    const t = toast.loading(`AI is reading your ${pages.length === 1 ? "photo" : `${pages.length} pages`}…`);
    try {
      const inv = await extractPhotos(pages.map((p) => p.file), user);
      toast.dismiss(t);
      toast.success(`${inv.supplier || "Invoice"} read — ${inv.flagged ? "needs a quick check" : "coded automatically"}`);
      pages.forEach((p) => URL.revokeObjectURL(p.url)); setPages([]);
      navigate(inv.status === "approved" || !inv.flagged ? `/invoice/${inv.id}` : `/approve?invoice=${inv.id}`);
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Couldn't read the photo — try better light and keep the whole invoice in frame");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4 sm:p-5" data-testid="camera-capture">
      <input ref={inputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" data-testid="camera-input" onChange={(e) => addPhotos(e.target.files)} />
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Camera className="h-5 w-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="font-medium">Snap a paper invoice</div>
          <p className="text-xs text-muted-foreground">Photograph it with your phone — one photo per page — and D20 reads it on the spot. Pages are kept as one PDF so it attaches to your accounting system like any upload.</p>
        </div>
      </div>

      {pages.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="camera-pages">
          {pages.map((p, i) => (
            <div key={p.url} className="relative h-28 w-20 overflow-hidden rounded-lg border border-border bg-muted" data-testid={`camera-page-${i + 1}`}>
              <img src={p.url} alt={`Page ${i + 1}`} className="h-full w-full object-cover" />
              <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 font-mono text-[10px] text-white">p{i + 1}</span>
              {!busy && <button type="button" onClick={() => remove(i)} aria-label={`Remove page ${i + 1}`} data-testid={`camera-page-remove-${i + 1}`} className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"><X className="h-3 w-3" /></button>}
            </div>
          ))}
          {pages.length < 10 && !busy && (
            <button type="button" onClick={() => inputRef.current?.click()} data-testid="camera-add-page" className="flex h-28 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-xs text-muted-foreground hover:border-primary/50 hover:text-foreground"><Plus className="h-4 w-4" /> Add page</button>
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {pages.length === 0 ? (
          <Button onClick={() => inputRef.current?.click()} disabled={disabled || busy} data-testid="camera-open-btn" className={cn("h-12 gap-2 px-5 text-base sm:h-10 sm:text-sm")}><Camera className="h-5 w-5 sm:h-4 sm:w-4" /> Take a photo</Button>
        ) : (
          <>
            <Button onClick={read} disabled={disabled || busy} data-testid="camera-read-btn" className="h-12 gap-2 px-5 text-base sm:h-10 sm:text-sm">{busy ? <Loader2 className="h-5 w-5 animate-spin sm:h-4 sm:w-4" /> : <ScanLine className="h-5 w-5 sm:h-4 sm:w-4" />} Read {pages.length === 1 ? "invoice" : `${pages.length} pages as one invoice`}</Button>
            <Button variant="ghost" onClick={() => { pages.forEach((p) => URL.revokeObjectURL(p.url)); setPages([]); }} disabled={busy} data-testid="camera-clear-btn">Start over</Button>
          </>
        )}
        <span className="hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> On a laptop this opens your webcam or file picker.</span>
      </div>
    </div>
  );
};
